import posthog from 'posthog-js';

const DEFAULT_API_HOST = '/api/mm';
const DEFAULT_UI_HOST = 'https://eu.posthog.com';
const MAX_SEARCH_LENGTH = 120;
const MAX_ERROR_MESSAGE_LENGTH = 200;
// Routes where scroll-depth tracking is meaningful (content pages, not utility/checkout flows).
const SCROLL_TRACKED_PREFIXES = [
  // The catalog itself lives at '/' and is handled by the explicit check in
  // isScrollTrackedRoute; only product pages need a prefix entry.
  '/library/product',
  '/tutorials',
  '/news',
  '/how-it-works',
  '/faq',
  '/top-contributors',
  '/affiliate-program',
  '/grid-cost-calculator',
  '/feedback-survey-en',
  '/feedback-survey-ru',
];
const SENSITIVE_ROUTE_QUERY_KEYS = new Set([
  'access',
  'auth',
  'code',
  'email',
  'password',
  'preview',
  'refresh',
  'secret',
  'token',
  'transaction_id',
]);
// transaction_id is intentionally NOT sensitive in events: the backend sends it raw,
// so the frontend keeps it raw too, allowing both sides to be joined on the same value.
const SENSITIVE_EVENT_PROPERTY_KEYS = new Set([
  'access',
  'auth',
  'code',
  'email',
  'password',
  'preview',
  'refresh',
  'secret',
  'token',
  'authorization',
  'csrfmiddlewaretoken',
  'first_name',
  'last_name',
  'username',
]);

let initialized = false;
let loaded = false;
let lastPageviewRouteKey = null;
let pendingPageview = null;
let lastIdentifiedDistinctId = null;

const parseSampleRate = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(1, parsed));
};

const compactProps = (props = {}) => Object.entries(props).reduce((acc, [key, value]) => {
  if (value === null || value === undefined || value === '') return acc;
  acc[key] = value;
  return acc;
}, {});

// Money/amount fields are sent as numbers so PostHog can Sum / average / P90 them.
// (The API returns decimals as strings.) Returns undefined for non-numeric so
// compactProps drops the key rather than sending NaN.
const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

// Error text is useful for debugging but may embed secrets passed up from the
// backend/Paddle (URLs with query tokens, emails, JWTs/long tokens). Redact those
// patterns before sending, then bound the length.
const ERROR_REDACTIONS = [
  [/https?:\/\/\S+/gi, '[url]'],
  [/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '[email]'],
  // Long opaque strings = tokens/JWTs/base64 (incl. + / = padding and url-safe variants).
  [/[A-Za-z0-9._\-+/=]{20,}/g, '[token]'],
];

const redactErrorMessage = (value) => {
  const redacted = ERROR_REDACTIONS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    String(value),
  );
  return redacted.slice(0, MAX_ERROR_MESSAGE_LENGTH);
};

export const sanitizeEventProps = (props = {}) => Object.entries(props || {}).reduce((acc, [key, value]) => {
  const normalizedKey = String(key).toLowerCase();

  if (normalizedKey === 'error_message') {
    if (value) acc.error_message = redactErrorMessage(value);
    return acc;
  }

  if (SENSITIVE_EVENT_PROPERTY_KEYS.has(normalizedKey)) {
    return acc;
  }

  if (Array.isArray(value)) {
    acc[key] = value.map((item) => (
      item && typeof item === 'object' && !Array.isArray(item)
        ? sanitizeEventProps(item)
        : item
    ));
    return acc;
  }

  if (value && typeof value === 'object') {
    acc[key] = sanitizeEventProps(value);
    return acc;
  }

  acc[key] = value;
  return acc;
}, {});

const sendPendingPageview = (posthog) => {
  if (!pendingPageview) return;
  posthog.capture('$pageview', pendingPageview);
  pendingPageview = null;
};

export const sanitizeRouteKey = (routeKey) => {
  const [path, rawQuery = ''] = String(routeKey || '').split('?');
  const queryKeys = [];
  if (rawQuery) {
    const params = new URLSearchParams(rawQuery);
    params.forEach((_, key) => {
      const normalizedKey = key.toLowerCase();
      if (!SENSITIVE_ROUTE_QUERY_KEYS.has(normalizedKey) && !queryKeys.includes(key)) {
        queryKeys.push(key);
      }
    });
  }
  queryKeys.sort();
  return {
    path,
    route_key: queryKeys.length > 0 ? `${path}?${queryKeys.join('&')}` : path,
    query_keys: queryKeys,
  };
};

// posthog-js auto-attaches $current_url / $referrer (the full URL incl. query) to
// every event, bypassing our route sanitization. Strip the query string so secrets
// in URLs (OAuth code, reset/verify tokens, email) never reach PostHog. UTM params
// are kept by PostHog in separate $utm_* properties, so attribution is unaffected.
export const sanitizeUrlProperties = (properties = {}) => {
  ['$current_url', '$referrer'].forEach((key) => {
    const value = properties[key];
    if (typeof value === 'string' && value.includes('?')) {
      properties[key] = value.split('?')[0];
    }
  });
  return properties;
};

export const initAnalytics = ({
  apiKey = process.env.REACT_APP_POSTHOG_API_KEY,
  apiHost = process.env.REACT_APP_POSTHOG_API_HOST || DEFAULT_API_HOST,
  sessionRecordingSampleRate = process.env.REACT_APP_POSTHOG_SESSION_RECORDING_SAMPLE_RATE || 0,
} = {}) => {
  if (typeof window === 'undefined' || !apiKey || initialized) {
    return false;
  }

  // Use the bundled posthog-js instance (window.posthog kept as the call target so
  // it stays interceptable; ingest still routes through the /api/mm proxy via api_host).
  window.posthog = window.posthog || posthog;
  if (typeof window.posthog.init !== 'function') {
    return false;
  }

  const recordingSampleRate = parseSampleRate(sessionRecordingSampleRate, 0);
  initialized = true;
  window.posthog.init(apiKey, {
    api_host: apiHost,
    ui_host: DEFAULT_UI_HOST,
    autocapture: false,
    // Pageviews are sent manually (one per route_key, query stripped). Pageleave is
    // left to posthog-js so PostHog can compute session duration / bounce rate — it
    // fires on tab close / navigation and only carries $current_url (sanitized below).
    capture_pageview: false,
    capture_pageleave: true,
    // Core Web Vitals (LCP/INP/CLS) — real-user perf + Google ranking signal. ~1 event
    // per page load; can be sampled later via before_send if volume grows.
    capture_performance: { web_vitals: true },
    // localStorage (not cookies) gives a stable distinct_id across reloads/sessions
    // so a returning visitor is one person, not many. No cookie => no consent banner.
    persistence: 'localStorage',
    // DNT not honored: the backend already ignores it for operational (api_request) and
    // financial (payment_confirmed = revenue source of truth) events, so respecting it
    // only on the frontend was inconsistent. Privacy is instead enforced uniformly — no
    // cookies, hashed distinct_ids, PII stripping. Policy: "track everyone, privately".
    respect_dnt: false,
    sanitize_properties: sanitizeUrlProperties,
    // Explicitly disable recording when the rate is 0 so adding the API key never
    // silently turns on session capture.
    disable_session_recording: recordingSampleRate <= 0,
    session_recording: {
      sampleRate: recordingSampleRate,
      maskAllInputs: true,
    },
    loaded: (posthog) => {
      loaded = true;
      posthog.register({
        app: 'gridsdb',
        source: 'web',
      });
      sendPendingPageview(posthog);
    },
  });

  return true;
};

export const capturePageview = (routeKey, extraProps = {}) => {
  if (!initialized || !routeKey) {
    return false;
  }

  const sanitizedRoute = sanitizeRouteKey(routeKey);
  if (sanitizedRoute.route_key === lastPageviewRouteKey) {
    return false;
  }
  lastPageviewRouteKey = sanitizedRoute.route_key;
  const props = compactProps({
    source: 'web',
    ...sanitizedRoute,
    ...extraProps,
  });

  if (!loaded) {
    pendingPageview = props;
    return true;
  }

  window.posthog.capture('$pageview', props);
  return true;
};

export const capture = (event, props = {}) => {
  if (!initialized || typeof window === 'undefined' || !window.posthog?.capture) {
    return false;
  }

  window.posthog.capture(event, compactProps(sanitizeEventProps({
    source: 'web',
    ...props,
  })));
  return true;
};

// Link the anonymous browser session to the authenticated user. The distinctId is
// computed server-side (salted hash) and matches the backend's distinct_id scheme,
// so frontend and backend events land on the same PostHog person.
export const identifyUser = (distinctId) => {
  if (!initialized || !distinctId || typeof window === 'undefined' || !window.posthog?.identify) {
    return false;
  }
  if (distinctId === lastIdentifiedDistinctId) {
    return false;
  }
  lastIdentifiedDistinctId = distinctId;
  window.posthog.identify(distinctId);
  return true;
};

// Call on logout so a shared device does not attribute the next visitor to the
// previous user.
export const resetAnalytics = () => {
  if (!lastIdentifiedDistinctId) {
    return false;
  }
  lastIdentifiedDistinctId = null;
  if (typeof window !== 'undefined' && window.posthog?.reset) {
    window.posthog.reset();
  }
  return true;
};

export const isScrollTrackedRoute = (pathname = '') => {
  if (pathname === '/') return true;
  return SCROLL_TRACKED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
};

export const normalizeSearchTerm = (value) => {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim().slice(0, MAX_SEARCH_LENGTH);
};

export const buildProductAnalyticsProps = (product = {}) => ({
  product_id: product.id,
  product_slug: product.slug,
  access_type: product.access_type,
  purchased_tier: product.purchased_tier,
  has_serial: Boolean(product.serial_number),
  has_grids_price: product.grids_price !== null && product.grids_price !== undefined,
  has_full_price: product.full_price !== null && product.full_price !== undefined,
  camera_id: product.camera?.id,
  camera_brand: product.camera?.brand,
  camera_model: product.camera?.model_name,
  lens_id: product.lens?.id,
  lens_brand: product.lens?.brand,
  lens_model: product.lens?.model_name,
  lens_series: product.lens?.series,
  asset_count: Array.isArray(product.assets) ? product.assets.length : 0,
});

export const buildLibraryAnalyticsProps = ({
  searchParams,
  view,
  sort,
  resultCount,
  totalCount,
} = {}) => {
  const params = searchParams || new URLSearchParams();
  const searchQuery = normalizeSearchTerm(params.get('search') || '');
  const cameraIds = params.getAll('camera').filter(Boolean).sort();
  const lensSeriesKeys = params.getAll('lens_series').filter(Boolean).sort();
  const productIds = params.getAll('product').filter(Boolean).sort();
  const priceMin = params.get('full_price__gte') || '';
  const priceMax = params.get('full_price__lte') || '';
  const hasPriceFilter = Boolean(priceMin || priceMax);
  return {
    search_query: searchQuery,
    search_length: searchQuery.length,
    camera_count: cameraIds.length,
    camera_ids: cameraIds,
    lens_series_count: lensSeriesKeys.length,
    lens_series_keys: lensSeriesKeys,
    product_filter_count: productIds.length,
    product_filter_ids: productIds,
    access_type: params.get('access_type') || '',
    has_price_filter: hasPriceFilter,
    price_range_bucket: hasPriceFilter ? `${priceMin || 'min'}-${priceMax || 'max'}` : '',
    page: params.get('page') || '1',
    sort: sort || 'date',
    view: view || 'grid',
    result_count: resultCount,
    total_count: totalCount,
  };
};

export const buildAssetAnalyticsProps = (asset = {}) => ({
  asset_id: asset.id ?? asset.asset_id,
  asset_name: asset.name || asset.file?.name,
  asset_type_id: asset.asset_type?.id,
  asset_type_slug: asset.asset_type?.slug,
  asset_type_name: asset.asset_type?.name,
  asset_type_is_grid: asset.asset_type?.is_grid,
  asset_size_mb: asset.file_size ? Number((asset.file_size / (1024 * 1024)).toFixed(2)) : undefined,
  asset_format_count: Array.isArray(asset.formats) ? asset.formats.length : 0,
  asset_format_names: Array.isArray(asset.formats)
    ? asset.formats.map((format) => format?.name).filter(Boolean)
    : [],
});

export const buildOrderAnalyticsProps = (order = {}) => {
  const items = Array.isArray(order.items) ? order.items : [];
  const products = items.map((item) => item?.product).filter(Boolean);
  // Per-product breakdowns (camera/lens/access/slug) live on product_viewed /
  // cart_item_added where one event = one product and aggregate cleanly. Keep only
  // the core line-item arrays here.
  return {
    order_id: order.id,
    order_items_count: items.length,
    order_total_price: toNumber(order.total_price),
    order_final_amount: toNumber(order.final_amount),
    order_discount_amount: toNumber(order.discount_amount),
    order_product_ids: products.map((product) => product?.id).filter(Boolean),
    order_price_tiers: items.map((item) => item?.price_tier).filter(Boolean),
  };
};

export const getScrollDepthThresholdsCrossed = ({
  currentPercent,
  emittedThresholds,
  thresholds = [25, 50, 75, 90],
} = {}) => thresholds.filter((threshold) => (
  currentPercent >= threshold && !emittedThresholds?.has?.(threshold)
));

export const __resetAnalyticsForTests = () => {
  initialized = false;
  loaded = false;
  lastPageviewRouteKey = null;
  pendingPageview = null;
  lastIdentifiedDistinctId = null;
};
